import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

// Page-scoped UI state for `/admin/teams/:teamId` (state-shape §2 slice
// `admin-team-detail-page`). This slice is built incrementally per UC; UC-58
// (Archive) introduces the archive-confirm fields and UC-60 (Add Member) the
// add-member modal fields. UC-62 (Assign Team Lead) adds the designate-as-lead
// confirm fields. UC-64 (Assign to Client) adds the assignToClientModalOpen /
// assignToClientDraft fields. UC-61 (Remove Member) adds pendingRemoveMemberConfirm.
// Sibling UCs (UC-57 rename, UC-59 delete, UC-63/UC-65 leads/assignments) extend
// it with their own fields.
export type AdminTeamDetailPageState = {
  // UC-57 — inline rename mode on the Team identity header. The draft name
  // itself is held in the header's React Hook Form (not in Redux), mirroring
  // the Client rename sibling.
  renameMode: boolean;
  // UC-58 — archive confirmation dialog.
  pendingArchiveConfirm: boolean;
  // UC-59 — hard-delete irreversibility confirmation dialog.
  pendingDeleteConfirm: boolean;
  // UC-60 — Add Member modal state (§5.web-portal.3).
  addMemberModalOpen: boolean;
  addMemberDraft: {
    userIds: string[];
    // UC-62 compound — "Also add as Team Lead" toggle in the Add Member modal.
    asTeamLead: boolean;
  };
  // UC-61 — inline "Remove from team" confirm dialog (§5.web-portal.3).
  // Stores the userId and displayName of the member pending removal so the
  // confirm dialog can render the member's name without an extra fetch.
  pendingRemoveMemberConfirm: { userId: string; displayName: string } | null;
  // UC-62 — inline "Designate as Lead" confirm dialog (path a).
  // Stores the userId and displayName of the member pending designation so
  // the confirm dialog can render without an extra fetch.
  pendingDesignateAsLeadConfirm: { userId: string; displayName: string } | null;
  // UC-63 — inline "Remove as Lead" confirm dialog. Stores the userId and
  // displayName of the lead pending withdrawal (§5.web-portal.3). Disabled
  // when the lead is the last remaining one (INV-012 / narrowing #2).
  pendingRemoveAsLeadConfirm: { userId: string; displayName: string } | null;
  // UC-64 — Assign-to-Client modal state (§5.web-portal.3).
  assignToClientModalOpen: boolean;
  assignToClientDraft: {
    clientId: string | null;
  };
  // UC-65 — inline "Remove from Client" confirm dialog (§5.web-portal.3).
  // Stores the clientId and clientName of the assignment pending removal so
  // the confirm dialog can render the client label without an extra fetch.
  pendingRemoveFromClientConfirm: { clientId: string; clientName: string | null } | null;
  // Shared submit/error fields — set on submit, cleared on response.
  submitInProgress: boolean;
  lastErrorKey: string | null;
};

const initialState: AdminTeamDetailPageState = {
  renameMode: false,
  pendingArchiveConfirm: false,
  pendingDeleteConfirm: false,
  addMemberModalOpen: false,
  addMemberDraft: {
    userIds: [],
    asTeamLead: false,
  },
  pendingRemoveMemberConfirm: null,
  pendingDesignateAsLeadConfirm: null,
  pendingRemoveAsLeadConfirm: null,
  assignToClientModalOpen: false,
  assignToClientDraft: {
    clientId: null,
  },
  pendingRemoveFromClientConfirm: null,
  submitInProgress: false,
  lastErrorKey: null,
};

export const adminTeamDetailPageSlice = createSlice({
  name: 'adminTeamDetailPage',
  initialState,
  reducers: {
    // ── UC-57 rename ──────────────────────────────────────────────────────
    // Enters inline rename mode; clears any leftover submit/error state so the
    // form always opens clean.
    enterRenameMode(state) {
      state.renameMode = true;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    exitRenameMode(state) {
      state.renameMode = false;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    // ── UC-58 archive confirm ─────────────────────────────────────────────
    // Opens the confirmation dialog; clears any leftover submit/error state
    // from a previous interaction so the dialog always opens clean.
    openArchiveConfirm(state) {
      state.pendingArchiveConfirm = true;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    // Closes the dialog; clears submit/error state.
    closeArchiveConfirm(state) {
      state.pendingArchiveConfirm = false;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    // ── UC-59 delete confirm (irreversible) ──────────────────────────────
    openDeleteConfirm(state) {
      state.pendingDeleteConfirm = true;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeDeleteConfirm(state) {
      state.pendingDeleteConfirm = false;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    // ── UC-60 Add Member modal ────────────────────────────────────────────
    openAddMemberModal(state) {
      state.addMemberModalOpen = true;
      state.addMemberDraft = initialState.addMemberDraft;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeAddMemberModal(state) {
      state.addMemberModalOpen = false;
      state.addMemberDraft = initialState.addMemberDraft;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    setAddMemberDraftUserIds(state, action: PayloadAction<string[]>) {
      state.addMemberDraft.userIds = action.payload;
    },
    setAddMemberDraftAsTeamLead(state, action: PayloadAction<boolean>) {
      state.addMemberDraft.asTeamLead = action.payload;
    },
    // ── UC-61 remove-member confirm ───────────────────────────────────────
    // Opens the per-row confirmation dialog; clears leftover submit/error
    // state so the dialog always opens clean.
    openRemoveMemberConfirm(
      state,
      action: PayloadAction<{ userId: string; displayName: string }>,
    ) {
      state.pendingRemoveMemberConfirm = action.payload;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeRemoveMemberConfirm(state) {
      state.pendingRemoveMemberConfirm = null;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    // ── UC-62 designate-as-lead confirm (path a) ─────────────────────────
    openDesignateAsLeadConfirm(
      state,
      action: PayloadAction<{ userId: string; displayName: string }>,
    ) {
      state.pendingDesignateAsLeadConfirm = action.payload;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeDesignateAsLeadConfirm(state) {
      state.pendingDesignateAsLeadConfirm = null;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    // ── UC-63 remove-as-lead confirm ─────────────────────────────────────────
    openRemoveAsLeadConfirm(
      state,
      action: PayloadAction<{ userId: string; displayName: string }>,
    ) {
      state.pendingRemoveAsLeadConfirm = action.payload;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeRemoveAsLeadConfirm(state) {
      state.pendingRemoveAsLeadConfirm = null;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    // ── UC-64 Assign-to-Client modal ─────────────────────────────────────────
    openAssignToClientModal(state) {
      state.assignToClientModalOpen = true;
      state.assignToClientDraft = initialState.assignToClientDraft;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeAssignToClientModal(state) {
      state.assignToClientModalOpen = false;
      state.assignToClientDraft = initialState.assignToClientDraft;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    setAssignToClientDraftClientId(state, action: PayloadAction<string | null>) {
      state.assignToClientDraft.clientId = action.payload;
    },
    // ── UC-65 remove-from-client confirm ─────────────────────────────────────
    openRemoveFromClientConfirm(
      state,
      action: PayloadAction<{ clientId: string; clientName: string | null }>,
    ) {
      state.pendingRemoveFromClientConfirm = action.payload;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeRemoveFromClientConfirm(state) {
      state.pendingRemoveFromClientConfirm = null;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    setSubmitInProgress(state, action: PayloadAction<boolean>) {
      state.submitInProgress = action.payload;
    },
    setLastErrorKey(state, action: PayloadAction<string | null>) {
      state.lastErrorKey = action.payload;
    },
    resetPage() {
      return initialState;
    },
  },
});

export const {
  enterRenameMode,
  exitRenameMode,
  openArchiveConfirm,
  closeArchiveConfirm,
  openDeleteConfirm,
  closeDeleteConfirm,
  openAddMemberModal,
  closeAddMemberModal,
  setAddMemberDraftUserIds,
  setAddMemberDraftAsTeamLead,
  openRemoveMemberConfirm,
  closeRemoveMemberConfirm,
  openDesignateAsLeadConfirm,
  closeDesignateAsLeadConfirm,
  openRemoveAsLeadConfirm,
  closeRemoveAsLeadConfirm,
  openAssignToClientModal,
  closeAssignToClientModal,
  setAssignToClientDraftClientId,
  openRemoveFromClientConfirm,
  closeRemoveFromClientConfirm,
  setSubmitInProgress,
  setLastErrorKey,
  resetPage,
} = adminTeamDetailPageSlice.actions;

type AppRootState = {
  adminTeamDetailPage: AdminTeamDetailPageState;
};

export const selectAdminTeamDetailPage = (state: AppRootState) =>
  state.adminTeamDetailPage;
